/**
 * CSAT summary card for embedding in the Channel Scorecard.
 * Compact — shows overall CSAT score + 4x25% breakdown with icons (no emojis).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ClipboardList, ArrowRight, Users, GraduationCap, Package, Radio } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ['community', 'education', 'product', 'signal'];
const CAT_META = {
  community: { Icon: Users,         color: '#F43F5E', label: 'Community' },
  education: { Icon: GraduationCap, color: '#3B82F6', label: 'Education' },
  product:   { Icon: Package,       color: '#10B981', label: 'Product'   },
  signal:    { Icon: Radio,         color: '#F59E0B', label: 'Signal'    },
};

export default function CSATScorecardCard({ ibId, period, onJumpToCSAT }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!ibId) return;
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const url = `${API}/csat/scorecard/${ibId}${period ? `?month=${period}` : ''}`;
        const r = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        setData(r.data);
      } catch (e) {
        setData(null);
      }
    };
    load();
  }, [ibId, period]);

  if (!data) return null;

  const total = data.total_score || 0;
  const responses = data.response_count || 0;

  return (
    <div
      className="bg-gradient-to-br from-[#F43F5E]/10 to-[#F97316]/10 border border-[#F43F5E]/30 rounded-2xl p-5 cursor-pointer hover:border-[#F43F5E]/50 transition-all"
      onClick={onJumpToCSAT}
      data-testid="csat-scorecard-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#F43F5E]/20 flex items-center justify-center">
            <ClipboardList size={14} className="text-[#F43F5E]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white">Customer Satisfaction (CSAT)</p>
            <p className="text-[10px] text-[#9CA3AF]">{responses} response(s){period ? ` · ${period}` : ' · all-time'}</p>
          </div>
        </div>
        <ArrowRight size={14} className="text-[#F43F5E]" />
      </div>

      <div className="flex items-baseline gap-2 mb-3">
        <p className="text-4xl font-bold text-white tabular-nums leading-none">{total.toFixed(1)}<span className="text-lg text-[#6B7280]">%</span></p>
        <p className="text-[10px] text-[#F97316]">Standalone score</p>
      </div>

      <div className="grid grid-cols-4 gap-2">
        {CATEGORIES.map((c) => {
          const b = data.breakdown?.find(x => x.category === c);
          const s = b?.score_pct || 0;
          const weighted = (s * 0.25).toFixed(1);
          const CIcon = CAT_META[c].Icon;
          return (
            <div key={c} className="text-center" title={`${s.toFixed(1)}% × 25% = ${weighted}% contribution`}>
              <div className="w-6 h-6 mx-auto rounded-md flex items-center justify-center" style={{ backgroundColor: `${CAT_META[c].color}22` }}>
                <CIcon size={11} strokeWidth={2.5} style={{ color: CAT_META[c].color }} />
              </div>
              <p className="text-[10px] text-white tabular-nums mt-1 font-semibold">{s.toFixed(0)}%</p>
              <div className="mt-1 h-1 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${Math.min(100, s)}%`, backgroundColor: CAT_META[c].color }} />
              </div>
              <p className="text-[9px] text-[#F97316] mt-0.5 font-mono">({weighted}/25%)</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
